import {
  Directive,
  OnDestroy,
  ElementRef,
  Input,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import $ from 'jquery';

const HORIZONTAL = 'horizontal';
const VERTICAL = 'vertical';
let instance: any;

export class FloatingScroll {
  static floatingScroll(
    element: HTMLElement,
    method = 'init',
    options: any = {},
  ) {
    if (method === 'init') {
      const { orientation = HORIZONTAL, offset } = options;
      if (orientation !== HORIZONTAL && orientation !== VERTICAL) {
        throw new Error(
          `Scrollbar orientation should be either “${HORIZONTAL}” or “${VERTICAL}”`,
        );
      }
      instance = Object.create(this.floatingScrollProto).init(
        $(element),
        orientation,
        offset,
      );
    } else if (method === 'update') {
      instance.updateAPI();
      $(element).trigger(`${method}.fscroll`);
    } else if (method === 'destroy') {
      instance.destroyAPI();
      $(element).trigger(`${method}.fscroll`);
    }
    return element;
  }

  static floatingScrollProto = {
    getOrientationProps(orientation, offset) {
      const horizontal = orientation === HORIZONTAL;
      // Properties with “X” are for cross orientation (relative to the scrollbar orientation)
      return {
        ORIENTATION: orientation,
        SIZE: horizontal ? 'width' : 'height',
        X_SIZE: horizontal ? 'height' : 'width',
        OFFSET_SIZE: horizontal ? 'offsetWidth' : 'offsetHeight',
        OFFSET_X_SIZE: horizontal ? 'offsetHeight' : 'offsetWidth',
        CLIENT_SIZE: horizontal ? 'clientWidth' : 'clientHeight',
        CLIENT_X_SIZE: horizontal ? 'clientHeight' : 'clientWidth',
        INNER_X_SIZE: horizontal ? 'innerHeight' : 'innerWidth',
        SCROLL_SIZE: horizontal ? 'scrollWidth' : 'scrollHeight',
        SCROLL_POS: horizontal ? 'scrollLeft' : 'scrollTop',
        START: horizontal ? 'left' : 'top',
        X_START: horizontal ? 'top' : 'left',
        X_END: horizontal ? 'bottom' : 'right',
        OFFSET: offset,
      };
    },
    init(container, orientation, offset) {
      // tslint:disable-next-line: no-shadowed-variable
      const instance = this;
      instance.orientationProps = this.getOrientationProps(orientation, offset);
      const scrollBody = container.closest('.fl-scrolls-body');
      if (scrollBody.length) {
        instance.scrollBody = scrollBody;
      }
      instance.container = container[0];
      instance.visible = true;
      instance.initWidget();
      instance.updateAPI(); // recalculate scrollbar parameters and set its visibility
      instance.addEventHandlers();
      // Set skipSync flags to their initial values (because update() above calls syncWidget())
      instance.skipSyncContainer = instance.skipSyncWidget = false;
      return instance;
    },

    initWidget() {
      // tslint:disable-next-line: no-shadowed-variable
      const instance = this;
      const { ORIENTATION, SIZE, SCROLL_SIZE } = instance.orientationProps;
      const widget = (instance.widget = $(
        `<div class="fl-scrolls" data-orientation="${ORIENTATION}"></div>`,
      ));
      $('<div></div>')
        .appendTo(widget)
      [SIZE](instance.container[SCROLL_SIZE]);
      widget.appendTo(instance.container);
    },

    addEventHandlers() {
      // tslint:disable-next-line: no-shadowed-variable
      const instance = this;
      const eventHandlers = (instance.eventHandlers = [
        {
          $el: instance.scrollBody || $(window),
          handlers: {
            // Don’t use `$.proxy()` since it makes impossible event unbinding individually per instance
            // (see the warning at http://api.jquery.com/unbind/)
            scroll() {
              instance.updateAPI();
            },
            resize() {
              instance.updateAPI();
            },
          },
        },
        {
          $el: instance.widget,
          handlers: {
            scroll() {
              if (instance.visible && !instance.skipSyncContainer) {
                instance.syncContainer();
              }
              // Resume widget->container syncing after the widget scrolling has finished
              // (it might be temporally disabled by the container while syncing the widget)
              instance.skipSyncContainer = false;
            },
          },
        },
        {
          $el: $(instance.container),
          handlers: {
            scroll() {
              if (!instance.skipSyncWidget) {
                instance.syncWidget();
              }
              // Resume container->widget syncing after the container scrolling has finished
              // (it might be temporally disabled by the widget while syncing the container)
              instance.skipSyncWidget = false;
            },
            focusin() {
              setTimeout(() => instance.syncWidget(), 0);
            },
            'update.fscroll'({ namespace }) {
              // Check event namespace to ensure that this is not an extraneous event in a bubbling phase
              if (namespace === 'fscroll') {
                instance.updateAPI();
              }
            },
            'destroy.fscroll'({ namespace }) {
              if (namespace === 'fscroll') {
                instance.destroyAPI();
              }
            },
          },
        },
      ]);
      eventHandlers.forEach(({ $el, handlers }) => $el.bind(handlers));
    },

    checkVisibility() {
      // tslint:disable-next-line: no-shadowed-variable
      const instance = this;
      const { widget, container, scrollBody } = instance;
      const {
        SCROLL_SIZE,
        OFFSET_SIZE,
        X_START,
        X_END,
        INNER_X_SIZE,
        CLIENT_X_SIZE,
      } = instance.orientationProps;
      let mustHide = widget[0][SCROLL_SIZE] <= widget[0][OFFSET_SIZE];
      if (!mustHide) {
        const containerRect = container.getBoundingClientRect();
        const maxVisibleCrossEnd = scrollBody
          ? scrollBody[0].getBoundingClientRect()[X_END]
          : window[INNER_X_SIZE] || document.documentElement[CLIENT_X_SIZE];
        mustHide =
          containerRect[X_END] <= maxVisibleCrossEnd ||
          containerRect[X_START] > maxVisibleCrossEnd;
      }
      if (instance.visible === mustHide) {
        instance.visible = !mustHide;
        // We cannot simply hide the scrollbar since its scroll position won’t update in that case
        widget.toggleClass('fl-scrolls-hidden');
      }
    },

    syncContainer() {
      // tslint:disable-next-line: no-shadowed-variable
      const instance = this;
      const { SCROLL_POS, OFFSET } = instance.orientationProps;
      const scrollPos = instance.widget[0][SCROLL_POS];
      if (instance.container[SCROLL_POS] !== scrollPos) {
        // Prevents container’s “scroll” event handler from syncing back again widget scroll position
        instance.skipSyncWidget = true;
        // Note that this makes container’s “scroll” event handlers execute
        instance.container[SCROLL_POS] = scrollPos + OFFSET;
        // Jquery( instance.container).scrollLeft(scrollPos + OFFSET)
      }
    },

    syncWidget() {
      // tslint:disable-next-line: no-shadowed-variable
      const instance = this;
      const { SCROLL_POS } = instance.orientationProps;
      const scrollPos = instance.container[SCROLL_POS];
      if (instance.widget[0][SCROLL_POS] !== scrollPos) {
        // Prevents widget’s “scroll” event handler from syncing back again container scroll position
        instance.skipSyncContainer = true;
        // Note that this makes widget’s “scroll” event handlers execute
        instance.widget[0][SCROLL_POS] = scrollPos;
      }
    },

    // Recalculate scroll width/height and container boundaries
    updateAPI() {
      // tslint:disable-next-line: no-shadowed-variable
      const instance = this;
      const {
        SIZE,
        X_SIZE,
        OFFSET_X_SIZE,
        CLIENT_SIZE,
        CLIENT_X_SIZE,
        SCROLL_SIZE,
        START,
      } = instance.orientationProps;
      const { widget, container, scrollBody } = instance;
      const clientSize = container[CLIENT_SIZE];
      const scrollSize = container[SCROLL_SIZE];
      widget[SIZE](clientSize);
      if (!scrollBody) {
        widget.css(START, `${container.getBoundingClientRect()[START]}px`);
      }
      $('div', widget)[SIZE](scrollSize);

      // Fit widget size to the native scrollbar size if needed
      if (scrollSize > clientSize) {
        widget[X_SIZE](widget[0][OFFSET_X_SIZE] - widget[0][CLIENT_X_SIZE] + 1); // +1px JIC
      }
      instance.syncWidget();
      instance.checkVisibility(); // fixes issue #2
    },

    // Remove a scrollbar and all related event handlers
    destroyAPI() {
      // tslint:disable-next-line: no-shadowed-variable
      const instance = this;
      instance.eventHandlers.forEach(({ $el, handlers }) =>
        $el.unbind(handlers),
      );
      instance.widget.remove();
      instance.eventHandlers = instance.widget = instance.container = instance.scrollBody = null;
    },
  };
}

@Directive({
    selector: '[ngxFloatingScroll]',
    standalone: false
})
export class FloatingScrollDirective implements OnDestroy, OnChanges {
  private nativeElement: any;

  @Input() hScrollChanged: boolean;
  constructor(el: ElementRef) {
    this.nativeElement = <any>$(el.nativeElement);
  }

  ngOnDestroy() {
    FloatingScroll.floatingScroll(this.nativeElement, 'destroy');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['hScrollChanged'].firstChange) {
      setTimeout(() => {
        FloatingScroll.floatingScroll(this.nativeElement, 'init', {
          offset: 2,
        });
      });
      return;
    }

    if (
      changes['hScrollChanged'].previousValue &&
      changes['hScrollChanged'].previousValue !==
      changes['hScrollChanged'].currentValue
    ) {
      setTimeout(() => {
        FloatingScroll.floatingScroll(this.nativeElement, 'update');
      });
    }
  }
}
